import React, { useEffect, useState } from 'react';
import { api } from '../api';

const GoogleMap = ({ q }) => (
  <iframe
    title="Location Map"
    width="100%"
    height="320"
    loading="lazy"
    style={{ border: 0 }}
    src={`https://www.google.com/maps?q=${encodeURIComponent(q)}&output=embed`}
  />
);

const AppleMap = ({ q }) => (
  <iframe
    title="Location Map"
    width="100%"
    height="320"
    loading="lazy"
    style={{ border: 0 }}
    src={`https://maps.apple.com/?q=${encodeURIComponent(q)}`}
  />
);

const PublicHOA = ({ slug }) => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadProfile = async () => {
      try {
        console.log('🔍 Loading public profile for slug:', slug);
        // Public profile endpoint doesn't require authentication
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public-profile/by-slug/${slug}`);
        
        if (!response.ok) {
          if (response.status === 404) {
            setError('HOA profile not found');
          } else {
            setError('Failed to load profile');
          }
          return;
        }
        
        const data = await response.json();
        console.log('✅ Profile loaded:', data);
        setProfile(data);
      } catch (err) {
        console.error('❌ Error loading profile:', err);
        setError('Failed to load profile');
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      loadProfile();
    }
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="text-lg text-gray-600">Loading...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="text-xl text-red-600 mb-2">{error}</div>
          <a href="/" className="text-blue-600 hover:underline">Return to Home</a>
        </div>
      </div>
    );
  }

  const headerUrl = profile.header_image_id
    ? `${process.env.REACT_APP_BACKEND_URL}/api/public-profile/asset/${profile.header_image_id}`
    : null;

  const addr = profile.address || {};
  const fullAddr = [
    addr.line1,
    addr.line2,
    `${addr.city || ''} ${addr.state || ''} ${addr.zip || ''}`.trim()
  ].filter(Boolean).join(', ');

  // Email - don't obfuscate, just show as mailto link
  const email = profile?.contact?.email;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header Image */}
      {headerUrl && (
        <div className="h-64 w-full overflow-hidden bg-gray-200">
          <img
            src={headerUrl}
            alt={profile.name}
            className="w-full h-full object-cover"
          />
        </div>
      )}

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-8 space-y-8">
        {/* Title Section */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">{profile.name}</h1>
          {profile.headline && (
            <p className="text-xl text-gray-600">{profile.headline}</p>
          )}
        </div>

        {/* External Payments Button */}
        {profile.external_payments_enabled && (
          <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg shadow-lg p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold mb-2">💳 Make a Payment</h2>
                <p className="text-blue-100">
                  Pay HOA dues, fees, and other charges securely online
                </p>
              </div>
              <button
                onClick={() => window.location.href = `/public/${slug}/pay`}
                className="bg-white text-blue-600 px-8 py-4 rounded-lg font-bold text-lg hover:bg-blue-50 transition-colors shadow-lg flex items-center gap-2"
              >
                <span>💳</span>
                <span>Pay Now</span>
                <span>→</span>
              </button>
            </div>
            <div className="mt-4 flex gap-4 text-sm text-blue-100">
              <span>✓ Secure Payment</span>
              <span>✓ Instant Receipt</span>
              <span>✓ Multiple Payment Types</span>
            </div>
          </div>
        )}

        {/* About Section */}
        {profile.about && (
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-3">About</h2>
            <p className="text-gray-700 whitespace-pre-wrap">{profile.about}</p>
          </div>
        )}

        {/* Contact & Location Grid */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* Location */}
          {fullAddr && (
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-3">Location</h2>
              <div className="text-gray-700">
                {fullAddr.split(', ').map((line, i) => (
                  <div key={i}>{line}</div>
                ))}
              </div>
            </div>
          )}

          {/* Contact */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-3">Contact</h2>
            <div className="text-gray-700 space-y-2">
              {profile?.contact?.phone && (
                <div className="flex items-center">
                  <span className="font-medium mr-2">Phone:</span>
                  <a href={`tel:${profile.contact.phone}`} className="text-blue-600 hover:underline">
                    {profile.contact.phone}
                  </a>
                </div>
              )}
              {email && (
                <div className="flex items-center">
                  <span className="font-medium mr-2">Email:</span>
                  <a href={`mailto:${email}`} className="text-blue-600 hover:underline">
                    {email}
                  </a>
                </div>
              )}
              {profile?.contact?.website && profile.contact.website.trim() !== '' && (
                <div className="flex items-center">
                  <span className="font-medium mr-2">Website:</span>
                  <a
                    href={profile.contact.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline"
                  >
                    {profile.contact.website}
                  </a>
                </div>
              )}
              
              {/* Owner Portal Login Link */}
              {slug && (
                <div className="flex items-center mt-2">
                  <span className="font-medium mr-2">Login to owner account here:</span>
                  <a
                    href="https://hoamanagerpro.us"
                    className="text-blue-600 hover:underline"
                  >
                    hoamanagerpro.us
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Map */}
        {profile.map?.show_map && profile.map?.place_query && (
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-3">Map</h2>
            {profile.map.provider === 'apple' ? (
              <AppleMap q={profile.map.place_query} />
            ) : (
              <GoogleMap q={profile.map.place_query} />
            )}
          </div>
        )}

        {/* Links */}
        {profile.links && profile.links.length > 0 && (
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-3">Quick Links</h2>
            <ul className="space-y-2">
              {profile.links.map((link, index) => (
                <li key={index}>
                  <a
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline flex items-center"
                  >
                    <span className="mr-2">→</span>
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Public Documents */}
        {profile.public_documents?.documents && profile.public_documents.documents.length > 0 && (
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <span className="text-2xl">📄</span>
              {profile.public_documents.section_title || 'Community Documents'}
            </h2>
            <div className="grid md:grid-cols-2 gap-3">
              {profile.public_documents.documents.map((doc) => (
                <a
                  key={doc.document_id}
                  href={`${process.env.REACT_APP_BACKEND_URL}/api/public-profile/document/${doc.document_id}/view`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 p-4 border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:shadow-md transition-all group"
                >
                  <span className="text-3xl group-hover:scale-110 transition-transform">📄</span>
                  <div className="flex-1">
                    <div className="font-medium text-gray-900 group-hover:text-blue-600">
                      {doc.title}
                    </div>
                    <div className="text-xs text-gray-500">{doc.category}</div>
                  </div>
                  <svg className="w-5 h-5 text-gray-400 group-hover:text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </a>
              ))}
            </div>
          </div>
        )}

        {/* Latest Newsletter */}
        {profile.show_latest_newsletter && profile.latest_newsletter && (
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg shadow-md p-6 border-2 border-blue-200">
            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <span className="text-2xl">📰</span>
              Latest Newsletter
            </h2>
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{profile.latest_newsletter.title}</h3>
              {profile.latest_newsletter.date && (
                <div className="text-sm text-gray-500 mb-4">
                  {new Date(profile.latest_newsletter.date).toLocaleDateString('en-US', { 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}
                </div>
              )}
              
              {/* PDF Download/View Button */}
              {profile.latest_newsletter.id && (
                <button
                  onClick={async () => {
                    try {
                      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/newsletter/${profile.latest_newsletter.id}/pdf`);
                      const data = await response.json();
                      if (data.success && data.url) {
                        window.open(data.url, '_blank');
                      } else {
                        alert('Unable to load newsletter PDF');
                      }
                    } catch (error) {
                      console.error('Error fetching newsletter:', error);
                      alert('Unable to load newsletter PDF');
                    }
                  }}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors cursor-pointer"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  View Newsletter PDF
                </button>
              )}
            </div>
          </div>
        )}

        {/* Upcoming Public Meeting */}
        {profile.show_upcoming_meeting && profile.upcoming_public_meeting && (
          <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg shadow-md p-6 border-2 border-purple-200">
            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <span className="text-2xl">📅</span>
              Upcoming Public Meeting
            </h2>
            <div className="bg-white rounded-lg p-5 shadow-sm space-y-3">
              {profile.upcoming_public_meeting.title && (
                <h3 className="text-lg font-semibold text-gray-900">{profile.upcoming_public_meeting.title}</h3>
              )}
              
              {(profile.upcoming_public_meeting.date || profile.upcoming_public_meeting.time) && (
                <div className="flex items-center gap-2 text-gray-700">
                  <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  {profile.upcoming_public_meeting.date && (
                    <span className="font-medium">
                      {new Date(profile.upcoming_public_meeting.date).toLocaleDateString('en-US', {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </span>
                  )}
                  {profile.upcoming_public_meeting.time && (
                    <span className="text-sm text-gray-600">
                      at {profile.upcoming_public_meeting.time}
                    </span>
                  )}
                </div>
              )}
              
              {profile.upcoming_public_meeting.location && (
                <div className="flex items-center gap-2 text-gray-700">
                  <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <span>{profile.upcoming_public_meeting.location}</span>
                </div>
              )}
              
              {profile.upcoming_public_meeting.agenda && (
                <div className="mt-3 pt-3 border-t border-gray-200">
                  <div className="flex items-center gap-2 text-gray-700">
                    <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <a
                      href={`${process.env.REACT_APP_BACKEND_URL}/api/public-profile/document/${profile.upcoming_public_meeting.agenda.document_id}/view`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-purple-600 hover:text-purple-800 font-medium underline"
                    >
                      📄 {profile.upcoming_public_meeting.agenda.title || 'View Meeting Agenda'}
                    </a>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="text-center text-gray-500 text-sm py-4">
          <p>Powered By HOA Manager Pro</p>
        </div>
      </div>
    </div>
  );
};

export default PublicHOA;
